import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Checkbox,
} from '@mui/material';
import {
  ArrowBack,
  Visibility,
  People,
  CalendarToday,
} from '@mui/icons-material';
import { viewsAPI } from '../services/api';
import { ProspectViewDetail, ProspectViewMember } from '../types';

// ── Props ─────────────────────────────────────────────────────────────────────

interface ViewPageProps {
  viewId: number;
  onBack: () => void;
  onRunFullSearch: (jobId: number, viewName: string) => void;
}

// ── Sort helpers ──────────────────────────────────────────────────────────────

type SortField = 'last_name' | 'affluence_score' | 'wealth_iq_score' | 'est_hh_income' | 'home_value';

function toSortable(val: string | number | null | undefined): number | string {
  if (val === null || val === undefined || val === '') return '';
  const stripped = String(val).replace(/[$,]/g, '');
  const num = parseFloat(stripped);
  return isNaN(num) ? String(val).toLowerCase() : num;
}

function compareName(a: ProspectViewMember, b: ProspectViewMember): number {
  const firstCmp = (a.first_name ?? '').localeCompare(b.first_name ?? '');
  if (firstCmp !== 0) return firstCmp;
  return (a.last_name ?? '').localeCompare(b.last_name ?? '');
}

function sortMembers(
  members: ProspectViewMember[],
  field: SortField,
  dir: 'asc' | 'desc',
): ProspectViewMember[] {
  return [...members].sort((a, b) => {
    if (field === 'last_name') {
      return dir === 'asc' ? compareName(a, b) : compareName(b, a);
    }
    const av = toSortable(a[field]);
    const bv = toSortable(b[field]);
    let cmp: number;
    if (typeof av === 'number' && typeof bv === 'number') {
      cmp = av - bv;
    } else {
      cmp = String(av).localeCompare(String(bv));
    }
    if (cmp !== 0) return dir === 'asc' ? cmp : -cmp;
    // tiebreak by last name then first name
    return compareName(a, b);
  });
}

// ── Component ─────────────────────────────────────────────────────────────────

const ViewPage: React.FC<ViewPageProps> = ({ viewId, onBack, onRunFullSearch }) => {
  const [view, setView] = useState<ProspectViewDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Sort state
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Row selection (for enrichment)
  const [selected, setSelected] = useState<Set<number>>(new Set());

  // Members table pagination
  const [viewPage, setViewPage] = useState(0);
  const [viewRowsPerPage, setViewRowsPerPage] = useState(100);

  // Resumable job banner
  type ActiveJob = { id: number; status: string; total_rows: number; completed_rows: number; failed_rows: number };
  const [activeJob, setActiveJob] = useState<ActiveJob | null>(null);

  // ── Load view ────────────────────────────────────────────────────────────────

  const loadView = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, job] = await Promise.all([
        viewsAPI.getView(viewId),
        viewsAPI.getActiveEnrichment(viewId),
      ]);
      setView(data);
      setActiveJob(job);
      // Default-select all members
      setSelected(new Set(data.members.map(m => m.id)));
    } catch (err: any) {
      setError(err.message || 'Failed to load view');
    } finally {
      setLoading(false);
    }
  }, [viewId]);

  useEffect(() => { loadView(); }, [loadView]);

  // ── Sort handler ─────────────────────────────────────────────────────────────

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setViewPage(0);
  };

  // ── Selection helpers ─────────────────────────────────────────────────────────

  // ── Band filter + derived ─────────────────────────────────────────────────────

  const filteredMembers: ProspectViewMember[] = view ? view.members : [];

  const allSelected = filteredMembers.length > 0 && filteredMembers.every(m => selected.has(m.id));
  const someSelected = filteredMembers.some(m => selected.has(m.id)) && !allSelected;

  const handleToggleAll = () => {
    if (allSelected) {
      setSelected(prev => { const n = new Set(prev); filteredMembers.forEach(m => n.delete(m.id)); return n; });
    } else {
      setSelected(prev => { const n = new Set(prev); filteredMembers.forEach(m => n.add(m.id)); return n; });
    }
  };

  const handleToggle = (id: number) => {
    setSelected(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  // ── Derived ──────────────────────────────────────────────────────────────────

  const sortedMembers = sortField ? sortMembers(filteredMembers, sortField, sortDir) : filteredMembers;
  const paginatedMembers = sortedMembers.slice(viewPage * viewRowsPerPage, viewPage * viewRowsPerPage + viewRowsPerPage);

  const scoreColumns: { label: string; field: SortField }[] = [
    { label: 'Affluence Score',    field: 'affluence_score'  },
    { label: 'WealthIQ Net Assets', field: 'wealth_iq_score' },
    { label: 'Est. HH Income',     field: 'est_hh_income'   },
    { label: 'Home Value',         field: 'home_value'       },
  ];

  // ── Helpers ──────────────────────────────────────────────────────────────────

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const dash = (
    <Typography component="span" sx={{ color: '#CBD5E0', fontSize: '0.78rem' }}>—</Typography>
  );

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', backgroundColor: '#E5E7EB' }}>

      {/* ── Page header ── */}
      <Box sx={{
        p: 3,
        backgroundColor: '#283E56',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={onBack} sx={{ color: 'white' }}>
            <ArrowBack />
          </IconButton>
          <Visibility sx={{ fontSize: 28 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {view?.name ?? 'Prospect View'}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.75 }}>
              Prospect view — select rows and run enrichment APIs
            </Typography>
          </Box>
        </Box>

        {view && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              icon={<People sx={{ fontSize: '16px !important', color: 'white !important' }} />}
              label={`${view.member_count} prospect${view.member_count !== 1 ? 's' : ''}`}
              sx={{ backgroundColor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 600 }}
            />
            <Chip
              icon={<CalendarToday sx={{ fontSize: '14px !important', color: 'white !important' }} />}
              label={formatDate(view.created_at)}
              sx={{ backgroundColor: 'rgba(255,255,255,0.1)', color: 'white' }}
            />
          </Box>
        )}
      </Box>

      <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>

        {/* ── Loading / error states ── */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#283E56' }} />
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
        )}

        {/* ── Main content ── */}
        {!loading && view && (
          <>
            {/* ── Resume banner ── */}
            {activeJob && (activeJob.status === 'pending' || activeJob.status === 'running' || activeJob.status === 'completed') && (
              <Paper elevation={3} sx={{
                p: 2, mb: 2.5, borderRadius: 2,
                borderLeft: `4px solid ${activeJob.status === 'completed' ? '#16a34a' : '#283E56'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap',
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#283E56' }}>
                    {activeJob.status === 'completed'
                      ? `Enrichment complete — ${activeJob.completed_rows} of ${activeJob.total_rows} processed`
                      : `Enrichment in progress — ${activeJob.completed_rows} / ${activeJob.total_rows} processed`}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#6B7280' }}>
                    {activeJob.status === 'completed'
                      ? 'Click to view the results from the last run'
                      : 'This job was started earlier and is still running'}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => onRunFullSearch(activeJob.id, view.name)}
                  sx={{ borderColor: '#283E56', color: '#283E56', whiteSpace: 'nowrap' }}
                >
                  {activeJob.status === 'completed' ? 'View Results' : 'Resume Progress'}
                </Button>
              </Paper>
            )}

            {/* ── Enrichment action bar ── */}
            <Paper elevation={2} sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#283E56' }}>
                    {selected.size} of {view.member_count} selected
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#6B7280' }}>
                    Select prospects below, then run the full enrichment pipeline
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                  <Typography variant="caption" sx={{ color: '#6B7280', fontStyle: 'italic' }}>
                    Full search not available at this time
                  </Typography>
                  <Button
                    variant="contained"
                    sx={{ backgroundColor: '#283E56', '&:hover': { backgroundColor: '#1e2f41' } }}
                  >
                    Run Full Search ({selected.size})
                  </Button>
                </Box>
              </Box>
            </Paper>

            {/* ── Members table ── */}
            {view.members.length === 0 ? (
              <Alert severity="info">This view has no members.</Alert>
            ) : filteredMembers.length === 0 ? (
              <Alert severity="info">No prospects found.</Alert>
            ) : (
              <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{
                  px: 2.5, py: 2,
                  backgroundColor: '#283E56',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Prospects — {view.member_count}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography variant="caption" sx={{ opacity: 0.75 }}>
                      Click column headers to sort · Check rows to select for enrichment
                    </Typography>
                  </Box>
                </Box>

                <TableContainer sx={{ maxHeight: 600 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#F8FAFC' }}>
                        <TableCell padding="checkbox" sx={{ backgroundColor: '#F8FAFC', width: 44 }}>
                          <Checkbox
                            size="small"
                            checked={allSelected}
                            indeterminate={someSelected}
                            onChange={handleToggleAll}
                            sx={{ color: '#283E56', '&.Mui-checked': { color: '#283E56' }, '&.MuiCheckbox-indeterminate': { color: '#283E56' } }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#283E56', backgroundColor: '#F8FAFC', width: 50 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#283E56', backgroundColor: '#F8FAFC' }}>
                          <TableSortLabel
                            active={sortField === 'last_name'}
                            direction={sortField === 'last_name' ? sortDir : 'asc'}
                            onClick={() => handleSort('last_name' as SortField)}
                            sx={{ '&.Mui-active': { color: '#283E56' }, '& .MuiTableSortLabel-icon': { color: '#283E56 !important' } }}
                          >
                            Name
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#283E56', backgroundColor: '#F8FAFC' }}>Address</TableCell>
                        {scoreColumns.map(({ label, field }) => (
                          <TableCell key={field} sx={{ fontWeight: 700, color: '#283E56', backgroundColor: '#F8FAFC', whiteSpace: 'nowrap' }}>
                            <TableSortLabel
                              active={sortField === field}
                              direction={sortField === field ? sortDir : 'asc'}
                              onClick={() => handleSort(field as SortField)}
                              sx={{ '&.Mui-active': { color: '#283E56' }, '& .MuiTableSortLabel-icon': { color: '#283E56 !important' } }}
                            >
                              {label}
                            </TableSortLabel>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedMembers.map((m, idx) => {
                        const globalIdx = viewPage * viewRowsPerPage + idx;
                        const isChecked = selected.has(m.id);
                        return (
                          <TableRow
                            key={m.id}
                            onClick={() => handleToggle(m.id)}
                            sx={{
                              cursor: 'pointer',
                              backgroundColor: isChecked ? 'rgba(40,62,86,0.04)' : 'inherit',
                              '&:hover': { backgroundColor: 'rgba(40,62,86,0.07)' },
                            }}
                          >
                            <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                              <Checkbox
                                size="small"
                                checked={isChecked}
                                onChange={() => handleToggle(m.id)}
                                sx={{ color: '#283E56', '&.Mui-checked': { color: '#283E56' } }}
                              />
                            </TableCell>
                            <TableCell sx={{ color: '#9CA3AF', fontSize: '0.78rem' }}>{globalIdx + 1}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {[m.first_name, m.last_name].filter(Boolean).join(' ')}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.82rem', color: '#374151', whiteSpace: 'nowrap' }}>
                              {m.address ?? dash}
                            </TableCell>
                            {(['affluence_score', 'wealth_iq_score', 'est_hh_income', 'home_value'] as const).map(field => (
                              <TableCell key={field} sx={{ fontSize: '0.82rem', color: '#283E56', fontWeight: m[field] ? 600 : 400, whiteSpace: 'nowrap' }}>
                                {m[field] ?? dash}
                              </TableCell>
                            ))}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={sortedMembers.length}
                  page={viewPage}
                  rowsPerPage={viewRowsPerPage}
                  rowsPerPageOptions={[50, 100, 250]}
                  onPageChange={(_, p) => setViewPage(p)}
                  onRowsPerPageChange={e => { setViewRowsPerPage(parseInt(e.target.value, 10)); setViewPage(0); }}
                  sx={{ borderTop: '1px solid #E5E7EB' }}
                />
              </Paper>
            )}
          </>
        )}
      </Box>

    </Box>
  );
};

export default ViewPage;